import React from 'react';
import { Grid, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Checkbox, Box, Typography, Chip } from '@mui/material';
import { Edit, Delete, CalendarToday as CalendarIcon, Article as ArticleIcon, AttachMoney, ThumbUp, Visibility } from '@mui/icons-material';
import TagCoverCard from '../TagCoverCard/TagCoverCard';

const TagCoverGrid = ({
  tags,
  variant = 'grid',
  onTagClick,
  onEditTag,
  onDeleteTag,
  selectionMode = false,
  selectedTags = [],
  onTagSelect,
}) => {
  // Renderização em lista
  if (variant === 'list') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {tags.map((tag) => {
          const isSelected = selectedTags.includes(tag._id);
          const articleCount = Number(tag.articleCount ?? tag.articlesCount ?? 0);
          const totalValuation = Number(tag.totalValuation ?? 0);
          const totalLikes = Number(tag.totalLikes ?? 0);
          const totalViews = Number(tag.totalViews ?? 0);
          const createdAt = tag.createdAt ? new Date(tag.createdAt).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }) : '';

          // Helper para formatar números
          const formatNumber = (num) => {
            if (!num || num === 0) return '0';
            if (num >= 1000000000) return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
            if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
            if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
            return num.toString();
          };

          const formatCurrency = (value) => {
            if (!value || value === 0) return 'R$ 0';
            return 'R$ ' + new Intl.NumberFormat('pt-BR', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(value);
          };

          return (
            <Box
              key={tag._id || tag.id}
              onClick={() => onTagClick?.(tag)}
              sx={{
                display: 'flex',
                height: 140,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: isSelected ? 'action.selected' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: isSelected ? 'action.selected' : 'action.hover',
                  borderColor: 'primary.main',
                },
              }}
            >
              {/* Checkbox de seleção (se modo seleção ativo) */}
              {selectionMode && (
                <Box sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 10
                }}>
                  <Checkbox
                    checked={isSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTagSelect?.(tag._id);
                    }}
                    sx={{
                      bgcolor: 'rgba(0,0,0,0.5)',
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.7)',
                      }
                    }}
                  />
                </Box>
              )}

              {/* Imagem de capa lateral - 220px */}
              <Box sx={{
                width: 220,
                flexShrink: 0,
                borderRadius: '8px 0 0 8px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {tag.coverImage ? (
                  <img
                    src={tag.coverImage}
                    alt={tag.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://www.mensure.com.br/public/mensure_logo.jpg';
                    }}
                  />
                ) : (
                  <Box sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.800'
                  }}>
                    <Box sx={{ textAlign: 'center', color: 'grey.500' }}>
                      <Typography variant="caption">Sem imagem</Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Separador vertical */}
              <Box sx={{ width: 2, bgcolor: 'divider', flexShrink: 0 }} />

              {/* Conteúdo */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                {/* Área superior: Título + Descrição + Data */}
                <Box sx={{ p: 1.5, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {/* Título e Data */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        flex: 1
                      }}
                    >
                      {tag.name || 'Sem nome'}
                    </Typography>
                    {createdAt && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          flexShrink: 0,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {createdAt}
                      </Typography>
                    )}
                  </Box>

                  {/* Descrição */}
                  {tag.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.813rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {tag.description}
                    </Typography>
                  )}
                </Box>

                {/* Footer com glassmorphism - métricas + ações */}
                <Box sx={{
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.02)'
                    : 'rgba(0, 0, 0, 0.02)',
                  backdropFilter: 'none',
                  WebkitBackdropFilter: 'none',
                  px: 2,
                  py: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2
                }}>
                  {/* Lado esquerdo: Métricas */}
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexShrink: 0 }}>
                    {/* Artigos */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ArticleIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {articleCount}
                      </Typography>
                    </Box>

                    {/* Valoração */}
                    {totalValuation > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AttachMoney sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {formatCurrency(totalValuation)}
                        </Typography>
                      </Box>
                    )}

                    {/* Likes */}
                    {totalLikes > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ThumbUp sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {formatNumber(totalLikes)}
                        </Typography>
                      </Box>
                    )}

                    {/* Views */}
                    {totalViews > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Visibility sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {formatNumber(totalViews)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Lado direito: Ações */}
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditTag?.(tag);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <Edit sx={{ fontSize: '1rem' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteTag?.(tag);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'error.main',
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <Delete sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  // Renderização em grid (padrão)
  return (
    <Grid container spacing={1}>
      {tags.map((tag) => {
        const isSelected = selectedTags.includes(tag._id);

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={tag._id || tag.id}>
            <Box sx={{ position: 'relative' }}>
              {selectionMode && (
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTagSelect?.(tag._id);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 10,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.7)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: isSelected ? 'primary.main' : '#fff',
                    },
                  }}
                />
              )}
              <TagCoverCard
                tag={tag}
                onClick={onTagClick}
                onEdit={onEditTag}
                onDelete={onDeleteTag}
                isSelected={isSelected}
                selectionMode={selectionMode}
              />
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default TagCoverGrid;
